import React, {
  useState,
  useMemo,
  useEffect,
  useDeferredValue,
  useRef,
  useCallback,
} from "react";
import {
  Box,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Tooltip,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CloseIcon from "@mui/icons-material/Close";
import { TABLE_HEAD_POSITIONS } from "../../component/Methods";
import ClosePositionDialog from "./Popup/ClosePositionDialog";
import CloseStrategyConfirmDialog from "./Popup/CloseStrategyConfirmDialog";
import PositionsLotsExpandedTable from "./PositionsLotsExpandedTable";
import PositionsTableToolbar from "./PositionsTableToolbar";
import {
  PositionsColgroup,
  NUMERIC_POSITION_HEAD_IDS,
} from "./positionsTableConstants";
import {
  buildDisplayedPositions,
  getStrategiesWithPositiveAumAndPositions,
  filterFundsWithPositions,
  buildCloseStrategyWebSocketPayload,
  CLOSE_STRATEGY_ACTION,
} from "./positionsUtils";
import { downloadPositionsCsv } from "./positionsExport";
import { sendWebSocketMessage } from "../../hooks/useWebSocket";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import {
  updateLoading,
  updateLoadingMsg,
  addActiveRequests,
} from "../../Redux/Reducers/uiSlice";

const POSITION_ROW_HEIGHT = 40;
const LOT_HEADER_HEIGHT = 34;
const LOT_ROW_HEIGHT = 32;
const OVERSCAN_PX = 480;

const getRowHeight = (row, openRowIds) => {
  const hasLots = Array.isArray(row?.lots) && row.lots.length > 0;
  if (hasLots && openRowIds.has(row.id)) {
    return (
      POSITION_ROW_HEIGHT +
      LOT_HEADER_HEIGHT +
      row.lots.length * LOT_ROW_HEIGHT
    );
  }
  return POSITION_ROW_HEIGHT;
};

const findRowAtOffset = (offsets, y) => {
  let lo = 0;
  let hi = offsets.length - 2;
  if (hi < 0) return 0;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (offsets[mid] <= y) lo = mid;
    else hi = mid - 1;
  }
  return lo;
};

const PositionsTable = ({
  positions,
  originalPositions,
  funds,
  strategies,
  custodians,
  accountingFunds = [],
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(1000);
  const [selectedFund, setSelectedFund] = useState("");
  const [selectedStrategy, setSelectedStrategy] = useState("");
  /** Expanded lot rows keyed by stable position id — multiple rows can stay open. */
  const [openRowIds, setOpenRowIds] = useState(() => new Set());
  const [sortField, setSortField] = useState("tickerSymbol");
  const [sortDirection, setSortDirection] = useState("asc");
  const [closePositionDialogOpen, setClosePositionDialogOpen] = useState(false);
  const [selectedPositionForClose, setSelectedPositionForClose] = useState(null);
  const [closeStrategyDialogOpen, setCloseStrategyDialogOpen] = useState(false);
  const sortedFunds = useMemo(() => {
    const withPositions = filterFundsWithPositions(funds, originalPositions);
    return [...withPositions].sort((a, b) =>
      (a.name || a.abbreviation || a.fundId || "").localeCompare(
        b.name || b.abbreviation || b.fundId || ""
      )
    );
  }, [funds, originalPositions]);

  const strategiesForSelectedFund = useMemo(
    () =>
      getStrategiesWithPositiveAumAndPositions({
        accountingFunds,
        selectedFundId: selectedFund,
        strategies,
        positions: originalPositions,
      }),
    [selectedFund, accountingFunds, strategies, originalPositions]
  );

  useEffect(() => {
    if (selectedFund && !sortedFunds.some((f) => f.fundId === selectedFund)) {
      setSelectedFund("");
      setSelectedStrategy("");
      setOpenRowIds(new Set());
      setPage(0);
    }
  }, [selectedFund, sortedFunds]);

  useEffect(() => {
    if (!selectedFund) {
      setSelectedStrategy("");
      return;
    }
    if (
      selectedStrategy &&
      !strategiesForSelectedFund.some((s) => s.strategyId === selectedStrategy)
    ) {
      setSelectedStrategy("");
    }
  }, [selectedFund, selectedStrategy, strategiesForSelectedFund]);

  const showCloseStrategyButton = useMemo(() => {
    if (!selectedFund || !selectedStrategy) return false;
    return strategiesForSelectedFund.some((s) => s.strategyId === selectedStrategy);
  }, [selectedFund, selectedStrategy, strategiesForSelectedFund]);

  const closeStrategyFundLabel = useMemo(() => {
    const f = sortedFunds.find((x) => x.fundId === selectedFund);
    return f?.name || f?.abbreviation || selectedFund || "";
  }, [sortedFunds, selectedFund]);

  const closeStrategyStrategyLabel = useMemo(() => {
    const s = strategiesForSelectedFund.find((x) => x.strategyId === selectedStrategy);
    return s?.name || selectedStrategy || "";
  }, [strategiesForSelectedFund, selectedStrategy]);

  const handleConfirmCloseStrategyPreview = () => {
    dispatch(updateLoading(true));
    dispatch(updateLoadingMsg("Loading close strategy preview…"));
    dispatch(addActiveRequests(CLOSE_STRATEGY_ACTION));
    sendWebSocketMessage(
      JSON.stringify(
        buildCloseStrategyWebSocketPayload(selectedFund, selectedStrategy, false)
      )
    );
    setCloseStrategyDialogOpen(false);
  };

  const deferredSearch = useDeferredValue(search);

  const filteredPositions = useMemo(
    () =>
      buildDisplayedPositions({
        originalPositions,
        selectedFund,
        selectedStrategy,
        search: deferredSearch,
        sortField,
        sortDirection,
      }),
    [
      originalPositions,
      selectedFund,
      selectedStrategy,
      deferredSearch,
      sortField,
      sortDirection,
    ]
  );

  useEffect(() => {
    const maxPage = Math.max(
      0,
      Math.ceil((filteredPositions.length || 0) / rowsPerPage) - 1
    );
    if (page > maxPage) setPage(maxPage);
  }, [filteredPositions.length, rowsPerPage, page]);

  const toggleRow = (rowId) => {
    setOpenRowIds((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
    setPage(0);
  };

  const handleClosePosition = (position) => {
    if (Number(position?.investment) !== 4) return;
    setSelectedPositionForClose(position);
    setClosePositionDialogOpen(true);
  };

  const handleClosePositionDialogClose = () => {
    setClosePositionDialogOpen(false);
    setSelectedPositionForClose(null);
  };

  const handleExportCsv = () => {
    downloadPositionsCsv(filteredPositions);
  };

  const containerRef = useRef(null);
  const pendingScrollTop = useRef(0);
  const scrollRaf = useRef(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(800);

  const pageRows = useMemo(
    () =>
      filteredPositions.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
      ),
    [filteredPositions, page, rowsPerPage]
  );

  const rowOffsets = useMemo(() => {
    const offsets = new Array(pageRows.length + 1);
    offsets[0] = 0;
    for (let i = 0; i < pageRows.length; i += 1) {
      offsets[i + 1] = offsets[i] + getRowHeight(pageRows[i], openRowIds);
    }
    return offsets;
  }, [pageRows, openRowIds]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const updateHeight = () => setViewportHeight(el.clientHeight || 800);
    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (containerRef.current) containerRef.current.scrollTop = 0;
    setScrollTop(0);
  }, [
    page,
    rowsPerPage,
    deferredSearch,
    selectedFund,
    selectedStrategy,
    sortField,
    sortDirection,
  ]);

  const handleTableScroll = useCallback((event) => {
    pendingScrollTop.current = event.currentTarget.scrollTop;
    if (scrollRaf.current != null) return;
    scrollRaf.current = requestAnimationFrame(() => {
      scrollRaf.current = null;
      setScrollTop(pendingScrollTop.current);
    });
  }, []);

  useEffect(
    () => () => {
      if (scrollRaf.current != null) cancelAnimationFrame(scrollRaf.current);
    },
    []
  );

  const totalBodyHeight = rowOffsets[pageRows.length] || 0;
  const startIndex = findRowAtOffset(
    rowOffsets,
    Math.max(0, scrollTop - OVERSCAN_PX)
  );
  const endIndex = Math.min(
    pageRows.length,
    findRowAtOffset(
      rowOffsets,
      scrollTop + viewportHeight + OVERSCAN_PX
    ) + 1
  );
  const topPad = rowOffsets[startIndex] || 0;
  const bottomPad = Math.max(0, totalBodyHeight - (rowOffsets[endIndex] || 0));
  const visibleRows = pageRows.slice(startIndex, endIndex);

  return (
    <Box sx={{ width: "100%" }}>
      <PositionsTableToolbar
        theme={theme}
        search={search}
        onSearchChange={(value) => {
          setSearch(value);
          setPage(0);
        }}
        selectedFund={selectedFund}
        onFundChange={(value) => {
          setSelectedFund(value);
          setSelectedStrategy("");
          setOpenRowIds(new Set());
          setPage(0);
        }}
        sortedFunds={sortedFunds}
        selectedStrategy={selectedStrategy}
        onStrategyChange={(value) => {
          setSelectedStrategy(value);
          setOpenRowIds(new Set());
          setPage(0);
        }}
        strategiesForSelectedFund={strategiesForSelectedFund}
        strategyFilterDisabled={
          !selectedFund || strategiesForSelectedFund.length === 0
        }
        showCloseStrategyButton={showCloseStrategyButton}
        onCloseStrategyClick={() => setCloseStrategyDialogOpen(true)}
        onExportClick={handleExportCsv}
        positionCount={filteredPositions.length}
      />

      <CloseStrategyConfirmDialog
        open={closeStrategyDialogOpen}
        onClose={() => setCloseStrategyDialogOpen(false)}
        fundLabel={closeStrategyFundLabel}
        strategyLabel={closeStrategyStrategyLabel}
        onConfirm={handleConfirmCloseStrategyPreview}
      />

      <TableContainer
        ref={containerRef}
        onScroll={handleTableScroll}
        sx={{ height: "calc(100vh - 80px)", overflowY: "scroll" }}
      >
        <Table stickyHeader size="small" sx={{ tableLayout: "fixed", width: "100%" }}>
          <PositionsColgroup />
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 52, backgroundColor: theme.palette.grey[100] }} />
              {TABLE_HEAD_POSITIONS.map((headCell) => {
                const align =
                  headCell.id === "actions"
                    ? "center"
                    : NUMERIC_POSITION_HEAD_IDS.has(headCell.id)
                      ? "right"
                      : "left";
                return (
                  <TableCell
                    key={headCell.id}
                    align={align}
                    onClick={
                      headCell.id === "actions" ? undefined : () => handleSort(headCell.id)
                    }
                    sx={{
                      fontWeight: 600,
                      backgroundColor: theme.palette.grey[100],
                      color: theme.palette.text.primary,
                      borderBottom: `2px solid ${theme.palette.divider}`,
                      whiteSpace: "nowrap",
                      cursor: headCell.id === "actions" ? "default" : "pointer",
                      userSelect: "none",
                      "&:hover": {
                        backgroundColor:
                          headCell.id === "actions"
                            ? theme.palette.grey[100]
                            : theme.palette.grey[200],
                      },
                      position: "relative",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent:
                          align === "right"
                            ? "flex-end"
                            : align === "center"
                              ? "center"
                              : "flex-start",
                      }}
                    >
                      {headCell.label}
                      {headCell.id !== "actions" && sortField === headCell.id && (
                        <Box
                          component="span"
                          sx={{
                            ml: 0.5,
                            fontSize: "12px",
                            color: theme.palette.primary.main,
                            fontWeight: "bold",
                          }}
                        >
                          {sortDirection === "asc" ? "↑" : "↓"}
                        </Box>
                      )}
                    </Box>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {topPad > 0 && (
              <TableRow aria-hidden sx={{ height: topPad }}>
                <TableCell
                  colSpan={11}
                  sx={{ p: "0 !important", border: "none", height: topPad, lineHeight: 0 }}
                />
              </TableRow>
            )}
            {visibleRows.map((row) => {
                const hasLots = Array.isArray(row?.lots) && row?.lots.length > 0;
                const isOpen = openRowIds.has(row.id);

                return (
                  <React.Fragment key={row.id}>
                    <TableRow
                      sx={{
                        "&:hover": {
                          backgroundColor: theme.palette.action.hover,
                        },
                      }}
                    >
                      <TableCell sx={{ width: 52, py: 1 }}>
                        {hasLots && (
                          <IconButton
                            size="small"
                            onClick={() => toggleRow(row.id)}
                            sx={{ height: "20px", width: "20px" }}
                          >
                            {isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                          </IconButton>
                        )}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 500,
                          color: theme.palette.text.primary,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {row.positionType || "-"}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          color: theme.palette.primary.main,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {row.tickerSymbol ? row.tickerSymbol.toUpperCase() : "-"}
                      </TableCell>
                      <TableCell
                        sx={{
                          color: theme.palette.text.primary,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {row.investmentType || "-"}
                      </TableCell>
                      <TableCell
                        sx={{
                          color: theme.palette.text.primary,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {row.fundName || "-"}
                      </TableCell>
                      <TableCell
                        sx={{
                          color: theme.palette.text.primary,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {row.strategyName || "-"}
                      </TableCell>
                      <TableCell
                        sx={{
                          color: theme.palette.text.primary,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {row.custodianName || "-"}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontWeight: 500,
                          color:
                            row.shares >= 0 ? theme.palette.success.main : theme.palette.error.main,
                          fontVariantNumeric: "tabular-nums",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {row.shares
                          ? row.shares < 0
                            ? `(${Math.abs(row.shares).toLocaleString()})`
                            : row.shares.toLocaleString()
                          : "-"}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          color: theme.palette.text.primary,
                          fontVariantNumeric: "tabular-nums",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {row.averageCostPerShare ? `$${row.averageCostPerShare.toFixed(2)}` : "-"}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontWeight: 500,
                          color: theme.palette.text.primary,
                          fontVariantNumeric: "tabular-nums",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {row.price ? `$${row.price.toFixed(2)}` : "-"}
                      </TableCell>
                      <TableCell align="center" sx={{ px: 0.5 }}>
                        <Tooltip
                          title={
                            Number(row.investment) === 4
                              ? "Close this position"
                              : "Close is only available for equity positions"
                          }
                          arrow
                        >
                          <span>
                            <IconButton
                              size="small"
                              disabled={Number(row.investment) !== 4}
                              onClick={() => handleClosePosition(row)}
                              sx={{
                                color: theme.palette.error.main,
                                "&.Mui-disabled": {
                                  color: theme.palette.action.disabled,
                                },
                                "&:hover": {
                                  backgroundColor: theme.palette.error.light,
                                  color: theme.palette.error.contrastText,
                                },
                              }}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>

                    {hasLots && (
                      <TableRow>
                        <TableCell colSpan={11} sx={{ py: 0, px: 0, borderBottom: "none" }}>
                          <PositionsLotsExpandedTable
                            theme={theme}
                            isOpen={isOpen}
                            lots={row.lots}
                            strategies={strategies}
                            custodians={custodians}
                          />
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            {bottomPad > 0 && (
              <TableRow aria-hidden sx={{ height: bottomPad }}>
                <TableCell
                  colSpan={11}
                  sx={{ p: "0 !important", border: "none", height: bottomPad, lineHeight: 0 }}
                />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[25, 50, 100, 500, 1000]}
        component="div"
        count={filteredPositions.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sx={{
          borderTop: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.grey[100],
        }}
      />

      <ClosePositionDialog
        open={closePositionDialogOpen}
        onClose={handleClosePositionDialogClose}
        positionData={selectedPositionForClose}
      />
    </Box>
  );
};

export default PositionsTable;
